import { useEffect, useState } from 'react';
import { hasHealthyNativeDetector } from '../lib/barcode';
import { LiveViewfinder } from './LiveViewfinder';
import { StillCapture } from './StillCapture';

interface BarcodeScannerProps {
  onClose: () => void;
  onDecoded: (ean13: string) => void;
  triggerRef?: React.RefObject<HTMLElement>;
}

/**
 * Capability-gated barcode scanner (issue #167). Probes for a healthy native
 * `BarcodeDetector` (the same `ean_13`/`upc_a` check the decoder uses — never
 * UA sniffing) and routes:
 *
 *   - **Healthy native detector** (desktop/Android Chrome) → {@link LiveViewfinder}:
 *     the live `getUserMedia` viewfinder + continuous decode loop, unchanged.
 *   - **No healthy native detector** (iOS Safari, and any WebKit/iOS browser
 *     that falls to the polyfill) → {@link StillCapture}: tap-to-capture
 *     single-still decode. The polyfill live loop OOM-crashes the WebKit tab
 *     (its per-tab Jetsam memory kill); a one-shot still decode never does.
 *
 * The decoded-EAN contract (`onClose`/`onDecoded`/`triggerRef`) is identical on
 * both branches. While the async probe resolves nothing is rendered — it
 * settles within a microtask, so there is no perceptible flash, and the live
 * camera is never acquired on the WebKit path.
 */
export function BarcodeScanner(props: BarcodeScannerProps): React.ReactElement | null {
  const [mode, setMode] = useState<'probing' | 'live' | 'still'>('probing');

  useEffect(() => {
    let cancelled = false;
    hasHealthyNativeDetector()
      .then((healthy) => {
        if (!cancelled) setMode(healthy ? 'live' : 'still');
      })
      .catch(() => {
        if (!cancelled) setMode('still');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (mode === 'probing') return null;
  return mode === 'live' ? <LiveViewfinder {...props} /> : <StillCapture {...props} />;
}
